(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion:reduce)');
  const revealObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      entry.target.classList.remove('eh-reveal--waiting');
      revealObserver.unobserve(entry.target);
    });
  }, {rootMargin:'0px 0px -40px 0px'}) : null;
  const selectAll = (root, selector) => [
    ...(root instanceof Element && root.matches(selector) ? [root] : []),
    ...root.querySelectorAll(selector)
  ];
  const productQuoteSeeds = new WeakMap();
  const productQuoteChoices = (form, productInfo) => {
    const choices = new Map();
    const addChoice = (label, value) => {
      label = String(label || '').trim();
      value = String(value ?? '').trim();
      if (!label || label.startsWith('_') || !value) return;
      if (!choices.has(label)) choices.set(label, new Set());
      choices.get(label).add(value);
    };
    for (const [name, value] of new FormData(form)) {
      if (name.startsWith('properties[') && name.endsWith(']') && !(value instanceof File)) addChoice(name.slice(11, -1), value);
    }
    const optionFields = [...productInfo.querySelectorAll('input[key^="po_"], select[key^="po_"], textarea[key^="po_"]')].filter(field => {
      const container = field.closest('.po-option--container') || field;
      return !field.disabled && container.getClientRects().length > 0;
    });
    const optionLabels = new Set(optionFields.map(field => field.getAttribute('key').slice(3)));
    try {
      const selected = window.BSS_PO?.getSelectedOptions?.all?.();
      if (Array.isArray(selected)) selected.forEach(option => {
        if (optionLabels.has(option.label)) addChoice(option.label, option.value);
      });
    } catch {}
    const visibleChoices = new Map();
    optionFields.forEach(field => {
      if (['radio', 'checkbox'].includes(field.type) && !field.checked) return;
      if (['hidden', 'file', 'button', 'submit'].includes(field.type)) return;
      const label = field.getAttribute('key').slice(3);
      const values = field.tagName === 'SELECT' ? [...field.selectedOptions].map(option => option.value) : [field.value];
      values.forEach(value => {
        if (!String(value).trim()) return;
        if (!visibleChoices.has(label)) visibleChoices.set(label, new Set());
        visibleChoices.get(label).add(value);
      });
    });
    visibleChoices.forEach((values, label) => choices.set(label, values));
    return [...choices].map(([label, values]) => `${label}: ${[...values].join(', ')}`).join('\n');
  };
  const syncProductQuote = (items, form, productInfo) => {
    let seed = productQuoteSeeds.get(items);
    if (!seed) {
      const row = items.querySelector('[data-eh-quote-item]');
      if (!row) return;
      seed = {row, model: row.querySelector('[data-eh-quote-model]').value, values: new Map()};
      productQuoteSeeds.set(items, seed);
    }
    if (!items.contains(seed.row) || seed.row.querySelector('[data-eh-quote-model]').value !== seed.model) return;
    const values = [
      ['[data-eh-item-notes]', productQuoteChoices(form, productInfo)],
      ['[data-eh-item-quantity]', form.elements.namedItem('quantity')?.value || '1'],
      ['[data-eh-item-variant]', form.querySelector('[name=id]')?.value || '']
    ];
    values.forEach(([selector, value]) => {
      const field = seed.row.querySelector(selector);
      if (!seed.values.has(selector) || field.value === seed.values.get(selector)) {
        field.value = value;
        seed.values.set(selector, field.value);
      }
    });
    seed.row.querySelector('[data-eh-item-notes]').dispatchEvent(new Event('input', {bubbles:true}));
  };
  const initialize = (root = document) => {
    selectAll(root, '[data-eh-reveal]').forEach(element => {
      if (element.dataset.ehRevealReady) return;
      element.dataset.ehRevealReady = 'true';
      if (!revealObserver || reducedMotion.matches || window.Shopify?.designMode || document.body.dataset.ehRevealEnabled !== 'true') return;
      element.classList.add('eh-reveal');
      if (element.getBoundingClientRect().top >= window.innerHeight - 40) element.classList.add('eh-reveal--waiting');
      revealObserver.observe(element);
    });
    selectAll(root, '[data-eh-product-filter]').forEach(grid => {
      if (grid.dataset.ehFilterReady) return;
      grid.dataset.ehFilterReady = 'true';
      const buttons = [...grid.querySelectorAll('[data-eh-filter-value]')];
      const cards = [...grid.querySelectorAll('[data-eh-product-group]')];
      buttons.forEach(button => button.addEventListener('click', () => {
        const group = button.dataset.ehFilterValue;
        buttons.forEach(item => item.setAttribute('aria-pressed', String(item === button)));
        cards.forEach(card => { card.hidden = group !== 'all' && card.dataset.ehProductGroup !== group; });
      }));
    });
    selectAll(root, '[data-eh-quote-items]').forEach(items => {
      if (items.dataset.ehQuoteReady) return;
      items.dataset.ehQuoteReady = 'true';
      const rows = items.querySelector('[data-eh-quote-rows]');
      const template = items.querySelector('[data-eh-quote-template]');
      const add = items.querySelector('[data-eh-quote-add]');
      const live = items.querySelector('[data-eh-quote-live]');
      const summary = items.querySelector('[data-eh-quote-summary]');
      let nextIndex = rows.children.length;
      const updateReference = row => {
        const option = row.querySelector('[data-eh-quote-model]').selectedOptions[0];
        row.querySelector('[data-eh-item-url]').value = option?.dataset.productUrl || '';
        row.querySelector('[data-eh-item-variant]').value = option?.dataset.variantId || '';
        const description = row.querySelector('[data-eh-item-description]');
        description.textContent = row.querySelector('[data-eh-quote-model]').value;
        description.hidden = !description.textContent;
      };
      const updateSummary = () => {
        const allRows = [...rows.querySelectorAll('[data-eh-quote-item]')];
        allRows.forEach((row, index) => {
          row.querySelector('[data-eh-item-number]').textContent = String(index + 1);
          row.querySelectorAll('[data-eh-quote-key]').forEach(field => {
            field.name = `contact[Chair ${index + 1} ${field.dataset.ehQuoteKey}]`;
          });
          const remove = row.querySelector('[data-eh-quote-remove]');
          remove.hidden = allRows.length === 1;
          remove.setAttribute('aria-label', `${items.dataset.removeLabel} ${index + 1}`);
        });
        summary.value = allRows.map((row, index) => {
          const model = row.querySelector('[data-eh-quote-model]').value;
          const quantity = row.querySelector('[data-eh-item-quantity]').value;
          const notes = row.querySelector('[data-eh-item-notes]').value;
          const url = row.querySelector('[data-eh-item-url]').value;
          return `${index + 1}. ${model} | Quantity: ${quantity}${notes ? `\n${notes}` : ''}${url ? `\n${url}` : ''}`;
        }).join('\n\n');
      };
      rows.querySelectorAll('[data-eh-quote-item]').forEach(updateReference);
      rows.addEventListener('change', event => {
        if (event.target.matches('[data-eh-quote-model]')) updateReference(event.target.closest('[data-eh-quote-item]'));
        updateSummary();
      });
      rows.addEventListener('input', updateSummary);
      rows.addEventListener('click', event => {
        const remove = event.target.closest('[data-eh-quote-remove]');
        if (!remove || rows.children.length < 2) return;
        const row = remove.closest('[data-eh-quote-item]');
        const next = row.nextElementSibling || row.previousElementSibling;
        row.remove();
        updateSummary();
        live.textContent = items.dataset.removedMessage;
        next?.querySelector('[data-eh-quote-model]')?.focus();
      });
      add.hidden = false;
      add.addEventListener('click', () => {
        nextIndex += 1;
        const fragment = document.createElement('template');
        fragment.innerHTML = template.innerHTML.replaceAll('__row__', String(nextIndex));
        const row = fragment.content.querySelector('[data-eh-quote-item]');
        rows.append(fragment.content);
        updateReference(row);
        updateSummary();
        live.textContent = items.dataset.addedMessage;
        row.querySelector('[data-eh-quote-model]').focus();
      });
      items.closest('form')?.addEventListener('submit', updateSummary);
      updateSummary();
    });
    root.querySelectorAll('[data-eh-dialog]').forEach(button => {
      if (button.dataset.ehReady) return;
      button.dataset.ehReady = 'true';
      button.addEventListener('click', () => {
        const dialog = document.getElementById(button.dataset.ehDialog);
        if (!dialog) return;
        const productInfo = button.closest('product-info');
        const form = productInfo?.querySelector('product-form form');
        const items = dialog.querySelector('[data-eh-quote-items]');
        if (items && form) syncProductQuote(items, form, productInfo);
        dialog.showModal();
      });
    });
    root.querySelectorAll('dialog[data-eh-modal]').forEach(dialog => {
      if (dialog.dataset.ehReady) return;
      dialog.dataset.ehReady = 'true';
      dialog.querySelectorAll('a[href]').forEach(link => link.addEventListener('click', () => dialog.close()));
      dialog.querySelectorAll('[data-eh-close]').forEach(button => button.addEventListener('click', () => dialog.close()));
      dialog.addEventListener('click', event => { if (event.target === dialog) { const r = dialog.getBoundingClientRect(); if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) dialog.close(); } });
      const result = dialog.querySelector('[data-eh-form-result]');
      if (result && !dialog.open) dialog.showModal();
    });
    root.querySelectorAll('[data-eh-menu]').forEach(details => {
      if (details.dataset.ehReady) return;
      details.dataset.ehReady = 'true';
      let closeTimer;
      details.querySelector('summary')?.addEventListener('click', event => { if (window.matchMedia('(hover:hover)').matches && details.open && event.detail > 0) event.preventDefault(); });
      details.addEventListener('mouseenter', () => { clearTimeout(closeTimer); if (window.matchMedia('(hover:hover)').matches) details.open = true; });
      details.addEventListener('mouseleave', () => { closeTimer = setTimeout(() => { if (!details.contains(document.activeElement)) details.open = false; }, 180); });
      details.addEventListener('keydown', event => { if (event.key === 'Escape') { details.open = false; details.querySelector('summary').focus(); } });
      details.addEventListener('focusout', () => setTimeout(() => { if (!details.contains(document.activeElement)) details.open = false; }, 0));
    });
    root.querySelectorAll('[data-eh-card]').forEach(card => {
      if (card.dataset.ehReady) return;
      card.dataset.ehReady = 'true';
      const images = [...card.querySelectorAll('[data-eh-card-image]')];
      let index = 0;
      const dots = [...card.querySelectorAll('[data-eh-card-index]')];
      const showImage = next => {
        index = (next + images.length) % images.length;
        images.forEach((image, i) => { image.hidden = i !== index; });
        dots.forEach((dot, i) => dot.setAttribute('aria-current', i === index));
      };
      card.querySelectorAll('[data-eh-card-step]').forEach(button => button.addEventListener('click', event => { event.preventDefault(); event.stopPropagation(); showImage(index + Number(button.dataset.ehCardStep)); }));
      dots.forEach((dot, i) => dot.addEventListener('click', () => showImage(i)));
    });
    root.querySelectorAll('[data-eh-hero]').forEach(hero => {
      if (hero.dataset.ehReady) return;
      hero.dataset.ehReady = 'true';
      const slides = [...hero.querySelectorAll('[data-eh-slide]')];
      const dots = [...hero.querySelectorAll('[data-eh-slide-to]')];
      let index = 0;
      const show = i => { index = i; slides.forEach((slide, n) => { slide.classList.toggle('is-active', n === i); slide.inert = n !== i; }); dots.forEach((dot, n) => dot.setAttribute('aria-current', n === i)); };
      dots.forEach((dot, i) => dot.addEventListener('click', () => show(i)));
      show(0);
      if (hero.dataset.ehAutoplay === 'true' && slides.length > 1 && !matchMedia('(prefers-reduced-motion:reduce)').matches) {
        const interval = setInterval(() => { if (document.contains(hero) && !hero.matches(':hover') && !hero.contains(document.activeElement)) show((index + 1) % slides.length); else if (!document.contains(hero)) clearInterval(interval); }, Number(hero.dataset.ehInterval || 5) * 1000);
      }
    });
    root.querySelectorAll('[data-eh-article]').forEach(article => {
      if (article.dataset.ehReady) return;
      article.dataset.ehReady = 'true';
      const headings = [...article.querySelectorAll('.eh-article-copy h2')].filter(h => h.textContent.trim());
      const lists = article.querySelectorAll('[data-eh-toc-list]');
      headings.forEach((heading, index) => {
        if (!heading.id) heading.id = `${article.id}-section-${index + 1}`;
        lists.forEach(list => { const li = document.createElement('li'); const a = document.createElement('a'); a.href = `#${heading.id}`; a.textContent = heading.textContent.trim(); li.append(a); list.append(li); });
      });
      if (!headings.length) { article.querySelectorAll('.eh-toc').forEach(t => t.hidden = true); article.querySelector('.eh-article-layout')?.classList.add('eh-article-layout--plain'); }
      if ('IntersectionObserver' in window) { const observer = new IntersectionObserver(entries => { entries.forEach(entry => { if (entry.isIntersecting) article.querySelectorAll('[data-eh-toc-list] a').forEach(a => a.setAttribute('aria-current', a.hash === `#${entry.target.id}`)); }); }, {rootMargin:'-100px 0px -65% 0px'}); headings.forEach(h => observer.observe(h)); }
    });
  };
  document.addEventListener('DOMContentLoaded', () => initialize());
  document.addEventListener('product-info:loaded', event => initialize(event.target));
  new MutationObserver(records => records.forEach(record => record.addedNodes.forEach(node => { if (node instanceof HTMLElement) initialize(node); }))).observe(document.body, {childList:true, subtree:true});
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => selectAll(event.target, '[data-eh-reveal]').forEach(element => revealObserver?.unobserve(element)));
  document.addEventListener('shopify:section:select', event => selectAll(event.target, '[data-eh-reveal]').forEach(element => element.classList.remove('eh-reveal--waiting')));
  reducedMotion.addEventListener('change', () => {
    if (!reducedMotion.matches) return;
    document.querySelectorAll('.eh-reveal--waiting').forEach(element => {
      element.classList.remove('eh-reveal--waiting');
      revealObserver?.unobserve(element);
    });
  });
  document.addEventListener('click', event => { if (!event.target.closest('[data-eh-menu]')) document.querySelectorAll('[data-eh-menu][open]').forEach(menu => menu.open = false); });
  let lastScrollY = window.scrollY;
  const onScroll = () => {
    const currentScrollY = window.scrollY;
    document.querySelector('[data-eh-top]')?.classList.toggle('is-visible', currentScrollY > 500);
    document.querySelectorAll('.eh-header[data-eh-sticky="true"]').forEach(header => {
      const scrolled = currentScrollY > 200;
      const returning = currentScrollY < lastScrollY || header.contains(document.activeElement) || !!header.querySelector('details[open]');
      header.classList.toggle('is-scrolled', scrolled);
      if (!scrolled) header.classList.remove('is-returning');
      else if (Math.abs(currentScrollY - lastScrollY) > 2) header.classList.toggle('is-returning', returning);
    });
    lastScrollY = currentScrollY;
  };
  document.addEventListener('scroll', onScroll, {passive:true});
  document.addEventListener('click', event => { if (event.target.closest('[data-eh-top]')) window.scrollTo({top:0,behavior:matchMedia('(prefers-reduced-motion:reduce)').matches?'auto':'smooth'}); });
  if (document.readyState !== 'loading') initialize();
})();

class EhBlogSlider extends HTMLElement {
  connectedCallback() {
    if (this.track) return;
    this.track = this.querySelector('[data-eh-blog-track]');
    if (!this.track) return;
    this.controls = this.querySelector('[data-eh-blog-controls]');
    this.previous = this.querySelector('[data-eh-blog-prev]');
    this.next = this.querySelector('[data-eh-blog-next]');
    this.current = this.querySelector('[data-eh-blog-current]');
    this.total = this.querySelector('[data-eh-blog-total]');
    this.onPrevious = () => this.move(-1);
    this.onNext = () => this.move(1);
    this.onScroll = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; this.update(); });
    };
    this.onKey = event => {
      if (!this.active || event.target !== this.track || !['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'Home' || event.key === 'End') this.goTo(event.key === 'Home' ? 0 : this.maxScroll);
      else this.move((event.key === 'ArrowRight' ? 1 : -1) * this.direction);
    };
    this.previous?.addEventListener('click', this.onPrevious);
    this.next?.addEventListener('click', this.onNext);
    this.track.addEventListener('scroll', this.onScroll, {passive:true});
    this.track.addEventListener('keydown', this.onKey);
    this.onResize = () => this.measure();
    window.addEventListener('resize', this.onResize, {passive:true});
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.track);
    }
    this.measure();
  }

  measure() {
    this.active = (matchMedia('(max-width:767px)').matches ? this.dataset.mobileLayout : this.dataset.desktopLayout) === 'slider';
    const style = getComputedStyle(this.track);
    this.direction = style.direction === 'rtl' ? -1 : 1;
    const first = this.track.querySelector('.eh-post-card');
    this.step = first ? first.offsetWidth + (parseFloat(style.columnGap) || 0) : 1;
    this.maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    this.pages = Math.ceil(Math.max(0, this.maxScroll - 2) / Math.max(1, this.step)) + 1;
    this.controls.hidden = !this.active || this.maxScroll < 2;
    if (this.active && this.maxScroll > 1) {
      this.track.tabIndex = 0;
    } else this.track.removeAttribute('tabindex');
    this.update();
  }

  update() {
    const position = Math.abs(this.track.scrollLeft);
    const atEnd = position >= this.maxScroll - 2;
    this.previous.disabled = !this.active || position <= 2;
    this.next.disabled = !this.active || atEnd;
    const page = atEnd ? this.pages : Math.min(this.pages, Math.round(position / Math.max(1, this.step)) + 1);
    this.current.textContent = String(page);
    this.total.textContent = String(this.pages);
  }

  move(direction) {
    this.goTo(Math.max(0, Math.min(this.maxScroll, Math.abs(this.track.scrollLeft) + direction * this.step)));
  }

  goTo(position) {
    this.track.scrollTo({left:this.direction * position, behavior:matchMedia('(prefers-reduced-motion:reduce)').matches ? 'auto' : 'smooth'});
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.frame);
    this.resizeObserver?.disconnect();
    window.removeEventListener('resize', this.onResize);
    this.previous?.removeEventListener('click', this.onPrevious);
    this.next?.removeEventListener('click', this.onNext);
    this.track?.removeEventListener('scroll', this.onScroll);
    this.track?.removeEventListener('keydown', this.onKey);
    this.track = null;
  }
}

if (!customElements.get('eh-blog-slider')) customElements.define('eh-blog-slider', EhBlogSlider);
